import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import { useLiveChatSDK } from '@inno-sdk/livechat-sdk'
import { Item } from '@inno-sdks/files'
import { useWindowManager } from 'modules/desktop/pages/MainPage/components/WindowManager/hooks/useWindowManager'
import {
  useDesktopStore,
  useSelectionStore
} from 'modules/desktop/pages/MainPage/stores'
import { useNavigate } from 'react-router-dom'
import { CustomEvents } from 'shared/constants/customEvents'
import { usePreferencesStore } from 'shared/stores/preferencesStore'
import { DesktopGridItem } from 'shared/types/desktop-grid'
import { listenCustomEvent } from 'shared/utils/customEvents'

import {
  STILL_CONNECTING_HINT_MS,
  getSystemShortcutDefinition
} from '../../../constants'
import { useOpenItem } from '../../../hooks'
import { handleCustomShortcutAction } from '../../../utils'

type UseGridItemSelectionOptions = {
  item: Item
  itemMetaData: DesktopGridItem
  isRenaming: boolean
}

type GridItemClickEvent = Pick<
  MouseEvent<HTMLDivElement>,
  'metaKey' | 'ctrlKey' | 'shiftKey'
>

type GridItemKeyboardEvent = Pick<
  KeyboardEvent<HTMLDivElement>,
  'key' | 'preventDefault'
>

/**
 * Owns the click-to-select behaviour for a grid item: cmd/ctrl toggles, shift
 * range-selects, and a plain click either opens (single-click mode) or selects
 * (double-click mode). Also derives whether a group context menu applies.
 */
export const useGridItemSelection = ({
  item,
  itemMetaData,
  isRenaming
}: UseGridItemSelectionOptions) => {
  const openItem = useOpenItem()
  const { livechat } = useLiveChatSDK()
  const { openAppWindow } = useWindowManager()
  const navigate = useNavigate()
  const shortcutClickMethod = usePreferencesStore(
    state => state.shortcutClickMethod
  )
  const isSelected = useSelectionStore(state =>
    state.selectedIds.has(itemMetaData.id)
  )
  const selectionCount = useSelectionStore(state => state.selectedIds.size)
  const systemShortcutDefinition = getSystemShortcutDefinition(item)
  const systemShortcutAction = systemShortcutDefinition?.action
  const requiresLoadingState =
    systemShortcutDefinition?.requiresLoadingState === true
  const [isLoading, setIsLoading] = useState(false)
  const shortcutRequestIdRef = useRef<string | null>(null)
  const shortcutRequestSequenceRef = useRef(0)

  useEffect(() => {
    if (!systemShortcutAction || !requiresLoadingState) return

    const handleShortcutActionFinished = ({
      action,
      requestId
    }: {
      action: string
      requestId: string
    }) => {
      if (
        action !== systemShortcutAction ||
        requestId !== shortcutRequestIdRef.current
      )
        return

      shortcutRequestIdRef.current = null
      setIsLoading(false)
    }

    const stopListeningForReady = listenCustomEvent(
      CustomEvents.SHORTCUT_ACTION_READY,
      handleShortcutActionFinished
    )
    const stopListeningForFailure = listenCustomEvent(
      CustomEvents.SHORTCUT_ACTION_FAILED,
      handleShortcutActionFinished
    )

    return () => {
      stopListeningForReady()
      stopListeningForFailure()
    }
  }, [requiresLoadingState, systemShortcutAction])

  const isShortcutLoading = requiresLoadingState && isLoading
  const [showStillConnectingMessage, setShowStillConnectingMessage] =
    useState(false)

  useEffect(() => {
    if (!isShortcutLoading) return

    const timer = setTimeout(
      () => setShowStillConnectingMessage(true),
      STILL_CONNECTING_HINT_MS
    )
    return () => {
      clearTimeout(timer)
      setShowStillConnectingMessage(false)
    }
  }, [isShortcutLoading])
  // Right-clicking a selected item while several are selected opens a menu
  // that acts on the whole selection instead of just this item.
  const isGroupContext = isSelected && selectionCount > 1

  const openGridItem = useCallback(() => {
    if (isShortcutLoading) return

    const customAction = systemShortcutAction
    if (customAction) {
      if (requiresLoadingState) {
        const requestId = `${item.id}-${shortcutRequestSequenceRef.current}`
        shortcutRequestSequenceRef.current += 1
        shortcutRequestIdRef.current = requestId
        setIsLoading(true)
        handleCustomShortcutAction(customAction, {
          livechat,
          navigate,
          requestId
        })
      } else {
        handleCustomShortcutAction(customAction, { livechat, navigate })
      }
      return
    }

    if (itemMetaData.onClick.type === 'openApp') {
      openAppWindow(itemMetaData.onClick.appId, itemMetaData.onClick.url)
      return
    }
    openItem(item)
  }, [
    item,
    itemMetaData.onClick,
    livechat,
    navigate,
    openAppWindow,
    openItem,
    isShortcutLoading,
    requiresLoadingState,
    systemShortcutAction
  ])

  const handleOnClick = useCallback(
    (e: GridItemClickEvent) => {
      if (isRenaming || isShortcutLoading) return
      const selection = useSelectionStore.getState()

      if (e.metaKey || e.ctrlKey) {
        selection.toggle(itemMetaData.id)
        return
      }
      if (e.shiftKey) {
        const ordered = [...useDesktopStore.getState().items]
          .sort(
            (a, b) =>
              a.position.row - b.position.row || a.position.col - b.position.col
          )
          .map(i => i.id)
        selection.selectRange(ordered, itemMetaData.id)
        return
      }

      if (shortcutClickMethod === 'single') {
        // Single-click opens; opening clears the selection.
        selection.clear()
        openGridItem()
      } else {
        // Double-click mode: a plain click only selects this item.
        selection.selectOnly(itemMetaData.id)
      }
    },
    [
      itemMetaData.id,
      openGridItem,
      isRenaming,
      isShortcutLoading,
      shortcutClickMethod
    ]
  )

  const handleOnDoubleClick = useCallback(() => {
    if (isRenaming || isShortcutLoading) return
    if (shortcutClickMethod === 'double') {
      // Opening on double-click clears the selection left by the first click.
      useSelectionStore.getState().clear()
      openGridItem()
    }
  }, [openGridItem, isRenaming, isShortcutLoading, shortcutClickMethod])

  const handleOnKeyDown = useCallback(
    (e: GridItemKeyboardEvent) => {
      if (
        isRenaming ||
        isShortcutLoading ||
        (e.key !== 'Enter' && e.key !== ' ')
      )
        return
      e.preventDefault()
      useSelectionStore.getState().clear()
      openGridItem()
    },
    [isRenaming, isShortcutLoading, openGridItem]
  )

  return {
    isSelected,
    selectionCount,
    isGroupContext,
    isLoading: isShortcutLoading,
    showStillConnectingMessage: isShortcutLoading && showStillConnectingMessage,
    handleOnClick,
    handleOnDoubleClick,
    handleOnKeyDown
  }
}
